/**
 * Sprint 12 — Item-level reading panel.
 *
 * Two sub-sections: Reading strongest in / Reading softest in. Each
 * shows the 7 items with their dimension label, score (1–5), and
 * mode-aware display text (positive-form for reverse-scored strengths,
 * original wording for gaps).
 *
 * Collapse state persists per coach via localStorage. Default state
 * on first view is expanded so coaches see the new surface; once
 * they collapse it, the preference sticks.
 */
import React from "react";
import { api, formatApiError } from "../lib/api";

const STORAGE_KEY = "rt.coachPref.itemReading.expanded";

function loadExpanded() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === null ? true : v === "true";
  } catch {
    return true;
  }
}

function saveExpanded(v) {
  try {
    localStorage.setItem(STORAGE_KEY, String(v));
  } catch {
    /* ignore quota / disabled */
  }
}

function ItemRow({ item, context }) {
  const text = context === "strength" ? item.text_strength : item.text_gap;
  const borderColor = context === "strength" ? "#059669" : "#D97706";
  // 30% alpha on the left border per spec Part 9.
  const borderRgba = context === "strength" ? "rgba(5, 150, 105, 0.3)" : "rgba(217, 119, 6, 0.3)";
  return (
    <li
      data-testid={`item-row-${context}-${item.item_number}`}
      className="flex items-baseline gap-3 px-3 py-2"
      style={{
        background: "white",
        borderLeft: `3px solid ${borderRgba}`,
      }}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink leading-snug">{text}</div>
        <div
          className="eyebrow mt-1"
          style={{ color: "#7F7F7F", fontSize: "10px" }}
        >
          {item.dimension_label}
        </div>
      </div>
      <div
        className="text-base"
        style={{ fontFamily: "Oswald", fontWeight: 700, color: borderColor, minWidth: "28px", textAlign: "right" }}
      >
        {item.scored_value}
      </div>
    </li>
  );
}

export default function ItemReadingPanel({ respondentId }) {
  const [ranking, setRanking] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [expanded, setExpanded] = React.useState(loadExpanded);

  React.useEffect(() => {
    if (!respondentId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/api/respondents/${respondentId}/item-ranking`);
        if (!cancelled) setRanking(data);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [respondentId]);

  const toggle = () => {
    setExpanded((e) => {
      const next = !e;
      saveExpanded(next);
      return next;
    });
  };

  if (!respondentId) return null;

  return (
    <section data-testid="item-reading-panel" className="space-y-3">
      <button
        type="button"
        onClick={toggle}
        data-testid="item-reading-toggle"
        aria-expanded={expanded}
        className="w-full flex items-baseline justify-between px-1 py-2"
        style={{ background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}
      >
        <div>
          <div className="eyebrow" style={{ color: "#2563EB" }}>Item-level reading</div>
          <h3 className="text-2xl" style={{ fontFamily: "Oswald", fontWeight: 700, color: "#1A1A1A", marginTop: "4px" }}>
            Where they read strongest and softest
          </h3>
        </div>
        <div className="text-xs uppercase tracking-widest" style={{ color: "#7F7F7F", fontFamily: "Oswald" }}>
          {expanded ? "Hide" : "Show"}
        </div>
      </button>

      {expanded && (
        <div className="space-y-6" data-testid="item-reading-content">
          {loading && <div className="text-sm text-gray-500">Loading item-level reading…</div>}
          {error && (
            <div className="text-sm" style={{ color: "#AD393E" }} data-testid="item-reading-error">
              {error}
            </div>
          )}
          {ranking && (
            <>
              <div data-testid="item-reading-top">
                <div className="mb-2">
                  <div className="eyebrow" style={{ color: "#059669" }}>Reading strongest in</div>
                  <p className="text-sm text-gray-700 mt-1">
                    Across all 56 items, these are the readings that came back strongest.
                  </p>
                </div>
                <ul className="space-y-1" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {ranking.top_items.map((it) => (
                    <ItemRow key={`top-${it.item_number}`} item={it} context="strength" />
                  ))}
                </ul>
              </div>

              <div data-testid="item-reading-bottom">
                <div className="mb-2">
                  <div className="eyebrow" style={{ color: "#D97706" }}>Reading softest in</div>
                  <p className="text-sm text-gray-700 mt-1">
                    Across all 56 items, these are the readings that came back softest.
                  </p>
                </div>
                <ul className="space-y-1" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {ranking.bottom_items.map((it) => (
                    <ItemRow key={`bot-${it.item_number}`} item={it} context="gap" />
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
